import React, { useState, useEffect } from "react";
import "./Utilization.css";
import {
  GenericEngine,
  IconPause,
  IconPauseCircle,
  StatusAutonomyActive,
  StatusAutonomyNotReady,
  StatusAutonomyOff,
  StatusAutonomyReady,
  StatusEquipmentIdle,
  StatusEquipmentWorking,
} from "@deere/icons";
import AutonomyTimeline from "./AutonomyTimeline";

// Interface for DB autonomy log entries
interface AutonomyLogEntry {
  _id: string;
  metadata: {
    machineIdentifier: string;
    source: string;
  };
  timestamp: string;
  state: number; // Now always a number for both VADC and Navigator
}

// VADC state constants (from machine.proto)
const VADC_STATE = {
  OFF: 0,
  STANDBY: 1,
  ACTIVE: 2,
  LOW_ENERGY: 3,
  INVALID: 255,
};

// Navigator state constants (from machine.proto NavigatorStateEnum)
const NAV_STATE = {
  OFF: 0,
  GENERATING_PATH: 1,
  READY: 2,
  ENGAGED: 3,
  INTERRUPTED: 4,
};

const SvgSquare = ({ color }: { color: any }) => (
  <svg className="icon-svg" width="16" height="16">
    <rect width="16" height="16" fill={color} />
  </svg>
);
const DataBar = ({ percent, color }: { percent: any; color: any }) => (
  <div className="data-bar">
    <div className="data-bar-fill" style={{ width: percent, backgroundColor: color }} />
  </div>
);

function getStringTime(unixTime: number) {
  let formattedTime = "";
  if (unixTime) {
    const elapsed = Math.floor(unixTime / 1000); // Get elapsed time in seconds
    const days = Math.floor(elapsed / (3600 * 24));
    const hours = Math.floor((elapsed % (3600 * 24)) / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    if (days > 0) {
      formattedTime = `${days} days ${hours} hr`;
    } else if (hours > 0) {
      formattedTime = `${hours} hr ${minutes} min`;
    } else if (minutes > 0) {
      formattedTime = `${minutes} min ${seconds} sec`;
    } else {
      formattedTime = `${seconds} sec`;
    }
  } else {
    // formattedTime = `0 sec`;
    // console.log("Error, unix time unknown: ", unixTime);
    formattedTime = `0 sec`;
  }
  return formattedTime;
}

function Utilization({
  equipObject,
  jobsiteIdentifier,
}: {
  equipObject: any;
  jobsiteIdentifier: string;
}) {
  // State for API-fetched autonomy logs (combined)
  const [autonomyLogs, setAutonomyLogs] = useState<AutonomyLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch autonomy logs from API - single call, no source filter
  useEffect(() => {
    const fetchAutonomyLogs = async () => {
      const vehiclePortion = equipObject?.topicPortion;

      if (!jobsiteIdentifier || !vehiclePortion) {
        console.log("Missing required data:", { jobsiteIdentifier, vehiclePortion });
        setIsLoading(false);
        return;
      }

      // Clean up strings and build machineIdentifier
      const cleanTopicString = jobsiteIdentifier.replace(/\/+$/, "").replace(/\//g, "_");
      const cleanVehiclePortion = vehiclePortion.replace(/\//g, "_");
      const machineIdentifier = `${cleanTopicString}_${cleanVehiclePortion}`;

      try {
        // Single API call - fetch last 500 logs (no source filter)
        const response = await fetch("/API/AutonomyLogs/fetch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            machineIdentifier,
            limit: 500,
            offset: 0,
          }),
        });

        const data = await response.json();
        console.log("Autonomy Logs from DB:", data);

        if (data.logs && Array.isArray(data.logs)) {
          // Sort logs by timestamp ascending
          const sortedLogs = [...data.logs].sort(
            (a: AutonomyLogEntry, b: AutonomyLogEntry) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          );
          setAutonomyLogs(sortedLogs);
        }
      } catch (error) {
        console.error("Error fetching autonomy logs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchAutonomyLogs();

    // Set up polling interval - fetch every 1 minute (60000 ms)
    const intervalId = setInterval(() => {
      fetchAutonomyLogs();
    }, 60000);

    // Cleanup interval on unmount or dependency change
    return () => {
      clearInterval(intervalId);
    };
  }, [jobsiteIdentifier, equipObject?.topicPortion]);

  // console.log(equipObject?.equipObject);

  // autonomy state
  // EquipmentPanel state logic (from EquipmentPanel.tsx):
  // The panel reads the LATEST autonomyLog entry and checks which source it came from.
  // - If it's a VADC entry (has ["0"] field):
  //     ["0"] not "1"/"2" → Off
  //     ["0"] == "1"      → Ready
  //     ["0"] == "2"      → Working (falls to else)
  // - If it's a Navigator entry (has navState, no ["0"]):
  //     navState == "READY"       → Ready
  //     navState == "INTERRUPTED" → Paused
  //     else (ENGAGED, GENERATING_PATH, OFF) → Working
  //
  // The Utilization must replicate this: at each log entry, the display state is
  // determined by the SOURCE of that entry (whichever was last updated).

  let autonomyOffSeconds = 0;
  let autonomyReadySeconds = 0;
  let autonomyPausedSeconds = 0;
  let autonomyWorkingSeconds = 0;
  let autonomyTotalSeconds = 0;

  // Process autonomy logs from single API call
  const allLogs: { timestamp: number; source: "vadc" | "navigator"; state: number }[] = [];

  autonomyLogs.forEach((log) => {
    const rawSource = log.metadata?.source?.toLowerCase() || "vadc";
    const source: "vadc" | "navigator" = rawSource === "navigator" ? "navigator" : "vadc";
    allLogs.push({
      timestamp: new Date(log.timestamp).getTime(),
      source,
      state: typeof log.state === "number" ? log.state : 0,
    });
  });

  // Sort by timestamp ascending
  allLogs.sort((a, b) => a.timestamp - b.timestamp);

  const logLength = allLogs.length;

  // Single-pass: for each log entry, determine display state based on the
  // source of THAT entry (matching EquipmentPanel's "latest entry" behavior)
  for (let i = 0; i < logLength; i++) {
    const log = allLogs[i];

    // Time this entry is "active" until the next entry (or until now for the last one)
    const timeAmount =
      i < logLength - 1 ? allLogs[i + 1].timestamp - log.timestamp : Date.now() - log.timestamp;

    let displayState: "off" | "ready" | "paused" | "working";

    if (log.source === "navigator") {
      // Navigator entry: determine state from navigator state value
      // Matches EquipmentPanel logic: for Navigator entries, only READY and
      // INTERRUPTED have special handling; everything else falls to Working.
      // NAV_STATE: OFF=0, GENERATING_PATH=1, READY=2, ENGAGED=3, INTERRUPTED=4
      if (log.state === NAV_STATE.READY) {
        displayState = "ready";
      } else if (log.state === NAV_STATE.INTERRUPTED) {
        displayState = "paused";
      } else {
        // OFF(0), GENERATING_PATH(1), ENGAGED(3) → Working
        displayState = "working";
      }
    } else {
      // VADC entry: determine state from VADC state value
      // VADC_STATE: OFF=0, STANDBY=1, ACTIVE=2, LOW_ENERGY=3, INVALID=255
      if (log.state === VADC_STATE.STANDBY) {
        displayState = "ready";
      } else if (log.state === VADC_STATE.ACTIVE) {
        displayState = "working";
      } else {
        displayState = "off";
      }
    }

    switch (displayState) {
      case "off":
        autonomyOffSeconds += timeAmount;
        break;
      case "ready":
        autonomyReadySeconds += timeAmount;
        break;
      case "paused":
        autonomyPausedSeconds += timeAmount;
        break;
      case "working":
        autonomyWorkingSeconds += timeAmount;
        break;
    }
    autonomyTotalSeconds += timeAmount;
  }

  // Show loading state while fetching
  if (isLoading && autonomyLogs.length === 0) {
    return (
      <div className="utilization-container">
        <h2>Utilization</h2>
        <p>Loading autonomy logs...</p>
      </div>
    );
  }

  // Show message if no logs available
  if (!isLoading && autonomyLogs.length === 0) {
    return (
      <div className="utilization-container">
        <h2>Utilization</h2>
        <p>No autonomy logs available.</p>
      </div>
    );
  }

  return (
    <div className="utilization-container">
      {/* <button onClick={logAutonomyList}>
                Log Autonomy List
            </button> */}

      <h2>Utilization</h2>
      {/* top summary bar */}
      <div className="summary-bar">
        <div
          className="summary-segment work"
          // style={{ width: '80%' }}
          style={{
            width:
              (
                ((autonomyReadySeconds + autonomyWorkingSeconds) / autonomyTotalSeconds) *
                100
              ).toString() + "%",
          }}
        >
          {/* Green */}
          {/* 80% */}
          {((autonomyReadySeconds + autonomyWorkingSeconds) / autonomyTotalSeconds) * 100 > 10
            ? (
                ((autonomyReadySeconds + autonomyWorkingSeconds) / autonomyTotalSeconds) *
                100
              ).toFixed(0) + "%"
            : ""}
        </div>
        <div
          className="summary-segment idle"
          // style={{ width: '15%' }}
          style={{ width: ((autonomyPausedSeconds / autonomyTotalSeconds) * 100).toString() + "%" }}
        >
          {/* Orange */}
          {/* 15% */}
          {(autonomyPausedSeconds / autonomyTotalSeconds) * 100 > 10
            ? ((autonomyPausedSeconds / autonomyTotalSeconds) * 100).toFixed(0) + "%"
            : ""}
        </div>
        <div
          className="summary-segment middle"
          // style={{ width: '5%' }}
          style={{ width: ((autonomyOffSeconds / autonomyTotalSeconds) * 100).toString() + "%" }}
        >
          {/* Grey */}
          {/* 5% */}
          {(autonomyOffSeconds / autonomyTotalSeconds) * 100 > 10
            ? ((autonomyOffSeconds / autonomyTotalSeconds) * 100).toFixed(0) + "%"
            : ""}
        </div>
      </div>

      {/* <div className="details">
                <div className="row header-row">
                    <StatusEquipmentWorking icon={{ style: { width: "20px", height: "20px", margin: "0px 10px" } }} />
                    <div className="row-label">Working</div>
                    <div className="row-time">7 hr 25 min</div>
                    <div className="row-percent">80%</div>
                </div>
                <div className="row sub-row">
                    <div className="sub-label">Operator</div>
                    <div className="sub-time">2 hr</div>
                    <div className="sub-percent">30%</div>
                    <DataBar percent="30%" color="#4caf50" />
                </div>
                <div className="row sub-row">
                    <div className="sub-label">Autonomy</div>
                    <div className="sub-time">5 hr 25 min</div>
                    <div className="sub-percent">50%</div>
                    <DataBar percent="50%" color="#2196f3" />
                </div>
                <div className="row header-row">
                    <StatusAutonomyOff icon={{ style: { width: "20px", height: "20px", margin: "0px 10px" } }} />
                    <div className="row-label">Transport</div>
                    <div className="row-time">35 min</div>
                    <div className="row-percent">5%</div>
                </div>
                <div className="row sub-row">
                    <div className="sub-label">User Driven</div>
                    <div className="sub-time">35 min</div>
                    <div className="sub-percent">5%</div>
                    <DataBar percent="5%" color="#9e9e9e" />
                </div>
                <div className="row header-row">
                    <StatusEquipmentIdle icon={{ style: { width: "20px", height: "20px", margin: "0px 10px" } }} />
                    <div className="row-label">Idle</div>
                    <div className="row-time">1 hr</div>
                    <div className="row-percent">15%</div>
                </div>
                <div className="row sub-row">
                    <div className="sub-label">Operator</div>
                    <div className="sub-time">20 min</div>
                    <div className="sub-percent">5%</div>
                    <DataBar percent="5%" color="#ff9800" />
                </div>
                <div className="row sub-row">
                    <div className="sub-label">Autonomy</div>
                    <div className="sub-time">40 min</div>
                    <div className="sub-percent">10%</div>
                    <DataBar percent="10%" color="#87a3b9" />
                </div>
            </div> */}

      <div className="details">
        <div className="row header-row">
          <StatusEquipmentWorking
            icon={{ style: { width: "20px", height: "20px", margin: "0px 10px" } }}
          />
          <div className="row-label">Active</div>
          <div className="row-time">
            {/* 7 hr 25 min */}
            {getStringTime(autonomyReadySeconds + autonomyWorkingSeconds)}
          </div>
          <div className="row-percent">
            {/* 80% */}
            {(
              ((autonomyReadySeconds + autonomyWorkingSeconds) / autonomyTotalSeconds) *
              100
            ).toFixed(0)}
            %
          </div>
        </div>
        <div className="row sub-row">
          {/* <div className="sub-label">Operator</div> */}
          <div className="sub-label">Ready</div>
          <div className="sub-time">
            {/* 2 hr */}
            {getStringTime(autonomyReadySeconds)}
          </div>
          <div className="sub-percent">
            {/* 30% */}
            {((autonomyReadySeconds / autonomyTotalSeconds) * 100).toFixed(0)}%
          </div>
          <DataBar
            percent={((autonomyReadySeconds / autonomyTotalSeconds) * 100).toString() + "%"}
            color="#4caf50"
          />
        </div>
        <div className="row sub-row">
          <div className="sub-label">Working</div>
          <div className="sub-time">
            {/* 5 hr 25 min */}
            {getStringTime(autonomyWorkingSeconds)}
          </div>
          <div className="sub-percent">
            {/* 50% */}
            {((autonomyWorkingSeconds / autonomyTotalSeconds) * 100).toFixed(0)}%
          </div>
          <DataBar
            percent={((autonomyWorkingSeconds / autonomyTotalSeconds) * 100).toString() + "%"}
            color="#2196f3"
          />
        </div>

        <div className="row header-row">
          <StatusEquipmentIdle
            icon={{ style: { width: "20px", height: "20px", margin: "0px 10px" } }}
          />
          <div className="row-label">Inactive</div>
          <div className="row-time">
            {/* 1 hr */}
            {getStringTime(autonomyPausedSeconds)}
          </div>
          <div className="row-percent">
            {/* 15% */}
            {((autonomyPausedSeconds / autonomyTotalSeconds) * 100).toFixed(0)}%
          </div>
        </div>
        {/* <div className="row sub-row">
                    <div className="sub-label">Operator</div>
                    <div className="sub-time">
                        {getStringTime(autonomyPausedSeconds)}
                    </div>
                    <div className="sub-percent">
                        {((autonomyPausedSeconds) / autonomyTotalSeconds * 100)}%
                    </div>
                    <DataBar percent="5%" color="#ff9800" />
                </div> */}
        <div className="row sub-row">
          <div className="sub-label">Paused</div>
          <div className="sub-time">
            {/* 40 min */}
            {getStringTime(autonomyPausedSeconds)}
          </div>
          <div className="sub-percent">
            {/* 10% */}
            {((autonomyPausedSeconds / autonomyTotalSeconds) * 100).toFixed(0)}%
          </div>
          {/* Old color 87a3b9 */}
          <DataBar
            percent={((autonomyPausedSeconds / autonomyTotalSeconds) * 100).toString() + "%"}
            color="#ff9800"
          />
        </div>

        <div className="row header-row">
          <StatusAutonomyOff
            icon={{ style: { width: "20px", height: "20px", margin: "0px 10px" } }}
          />
          <div className="row-label">Off</div>
          <div className="row-time">
            {/* 35 min */}
            {getStringTime(autonomyOffSeconds)}
          </div>
          <div className="row-percent">
            {/* 5% */}
            {((autonomyOffSeconds / autonomyTotalSeconds) * 100).toFixed(0)}%
          </div>
        </div>
        <div className="row sub-row">
          <div className="sub-label">Inactive</div>
          <div className="sub-time">
            {/* 35 min */}
            {getStringTime(autonomyOffSeconds)}
          </div>
          <div className="sub-percent">
            {/* 5% */}
            {((autonomyOffSeconds / autonomyTotalSeconds) * 100).toFixed(0)}%
          </div>
          <DataBar
            percent={((autonomyOffSeconds / autonomyTotalSeconds) * 100).toString() + "%"}
            color="#9e9e9e"
          />
        </div>
      </div>

      {/* Total */}
      <div className="row total-row">
        <div className="total-label">Total</div>
        <div className="total-time" style={{ margin: "0px" }}>
          {/* 9 hr */}
          {getStringTime(autonomyTotalSeconds)}
        </div>
        {/* <div className="total-percent">100%</div> */}
      </div>

      <br />

      <AutonomyTimeline autonomyLogs={autonomyLogs} />
    </div>
  );
}

// const Utilization = (equipObject: any) => (

//     <div className="utilization-container">

//         <h2>Utilization</h2>
//         {/* top summary bar */}
//         <div className="summary-bar">
//             <div className="summary-segment work" style={{ width: '80%' }}>
//                 80%
//             </div>
//             <div className="summary-segment middle" style={{ width: '5%' }} />
//             <div className="summary-segment idle" style={{ width: '15%' }}>
//                 15%
//             </div>
//         </div>
//         {/* detailed breakdown */}
//         <div className="details">
//             {/* Working */}
//             <div className="row header-row">
//                 {/* <SvgSquare color="#4caf50" /> */}
//                 <StatusEquipmentWorking icon={{ style: { width: "20px", height: "20px", margin: "0px 10px" } }} />
//                 <div className="row-label">Working</div>
//                 <div className="row-time">7 hr 25 min</div>
//                 <div className="row-percent">80%</div>
//             </div>
//             <div className="row sub-row">
//                 <div className="sub-label">Operator</div>
//                 <div className="sub-time">2 hr</div>
//                 <div className="sub-percent">30%</div>
//                 <DataBar percent="30%" color="#4caf50" />
//             </div>
//             <div className="row sub-row">
//                 <div className="sub-label">Autonomy</div>
//                 <div className="sub-time">5 hr 25 min</div>
//                 <div className="sub-percent">50%</div>
//                 <DataBar percent="50%" color="#2196f3" />
//             </div>
//             {/* Transport */}
//             <div className="row header-row">
//                 {/* <SvgSquare color="#9e9e9e" /> */}
//                 <StatusAutonomyOff icon={{ style: { width: "20px", height: "20px", margin: "0px 10px" } }} />
//                 <div className="row-label">Transport</div>
//                 <div className="row-time">35 min</div>
//                 <div className="row-percent">5%</div>
//             </div>
//             {/* <div className="row bar-only-row">
//                 <DataBar percent="5%" color="#9e9e9e" />
//             </div> */}
//             <div className="row sub-row">
//                 <div className="sub-label">User Driven</div>
//                 <div className="sub-time">35 min</div>
//                 <div className="sub-percent">5%</div>
//                 <DataBar percent="5%" color="#9e9e9e" />
//             </div>
//             {/* Idle */}
//             <div className="row header-row">
//                 {/* <SvgSquare color="#ff9800" /> */}
//                 <StatusEquipmentIdle  icon={{ style: { width: "20px", height: "20px", margin: "0px 10px" } }} />
//                 <div className="row-label">Idle</div>
//                 <div className="row-time">1 hr</div>
//                 <div className="row-percent">15%</div>
//             </div>
//             <div className="row sub-row">
//                 <div className="sub-label">Operator</div>
//                 <div className="sub-time">20 min</div>
//                 <div className="sub-percent">5%</div>
//                 <DataBar percent="5%" color="#ff9800" />
//             </div>
//             <div className="row sub-row">
//                 <div className="sub-label">Autonomy</div>
//                 <div className="sub-time">40 min</div>
//                 <div className="sub-percent">10%</div>
//                 <DataBar percent="10%" color="#87a3b9" />
//             </div>
//         </div>
//         {/* Total */}
//         <div className="row total-row">
//             <div className="total-label">Total</div>
//             <div className="total-time">9 hr</div>
//             <div className="total-percent">100%</div>
//         </div>
//     </div>
// );
export default Utilization;
